import {View} from '@fantasmagames/slot/src/common/view/View';
import {StartScreenViewEvents} from '@fantasmagames/slot/src/modules/startscreen/StartScreenViewEvents';
import {Timer} from '@fantasmagames/core/src/update/Timer';
import {Spine} from '@fantasmagames/core/src/spine/Spine';
import {SpineButton} from '@fantasmagames/core/src/display/SpineButton';
import {SpineAnimationMultiTrack} from '@fantasmagames/core/src/display/SpineAnimationMultiTrack';
import {Position} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {Tween} from '@fantasmagames/core/src/tween/Tween';
import {Easing} from '@fantasmagames/core/src/tween/Easing';
import {SpineAnimation} from '@fantasmagames/core/src/display/SpineAnimation';
import {SpriteGraphics} from '@fantasmagames/core/src/display/SpriteGraphics';

export class StartScreenView extends View {
	public static readonly PLAY_BUTTON_CLICKED: string = 'StartScreenView::PLAY_BUTTON_CLICKED';
	protected _exitFinished: boolean = false;

	protected _introScreen: Spine;
	protected _introFeatures: Spine;
	protected _extraBackground: PIXI.Sprite;
	protected _currentFeature: number = 1;
	protected _playButton: SpineButton;
	protected _nextFeatureButton: SpineButton;
	protected _prevFeatureButton: SpineButton;

	protected _screenAnimations: SpineAnimationMultiTrack;
	protected _featuresAnimations: SpineAnimationMultiTrack;

	protected _playingExitAnimation: boolean = false;

	protected _featureTextStyle: PIXI.TextStyle;

	protected _totalFeatures: number;

	public init(): void {
		super.init();

		this.setupIntro();
		this.setupInput();
		this.setupTextStyle();
	}

	public translate(): void {
		let hasMorePages: boolean = true;
		let currentPage: number = 1;
		while (hasMorePages) {
			hasMorePages = !!this._introFeatures.skeleton.findBone(`FEATURE_${currentPage}`);
			if (hasMorePages) {
				let hasMoreTitles: boolean = true;
				let currentTitle: number = 1;
				while (hasMoreTitles) {
					hasMoreTitles = !!this.spineUtils.findSlotContainerByName(
						this._introFeatures,
						`FEATURE_${currentPage}_${currentTitle}_TITLE`,
					);
					if (hasMoreTitles) {
						this.spineUtils.replaceElementInSpine(
							this._introFeatures,
							`FEATURE_${currentPage}_${currentTitle}_TITLE`,
							new PIXI.Sprite(this.resources.getLocaleTextImage(`FEATURE_${currentPage}_${currentTitle}_TITLE.png`)),
						);
					}
					currentTitle++;
				}
			}
			currentPage++;
		}

		this._totalFeatures = currentPage - 2;

		for (let i = 1; i <= currentPage; i++) {
			const hasTextContainer: boolean = !!this.spineUtils.findSlotContainerByName(this._introFeatures, `FEATURE_${i}_TEXT`);
			if (hasTextContainer) {
				this.spineUtils.replaceElementInSpine(
					this._introFeatures,
					`FEATURE_${i}_TEXT`,
					this.createTranslatedTextObject(this.translator.getObject('BaseStrings', 'StartScreen', `page${i}`, 'text')),
				);
			}
		}
	}

	protected createTranslatedTextObject(text: string): PIXI.Text {
		const textObject = new PIXI.Text(text, this._featureTextStyle);

		return textObject;
	}

	protected setupTextStyle(): void {
		const featureBackgroundReference = this._introFeatures.skeleton.findSlot('TEXT_AREA').attachment;

		const descriptionWordWrapWidth: number =
			(featureBackgroundReference as PIXI.spine.core.RegionAttachment).width *
			(featureBackgroundReference as PIXI.spine.core.RegionAttachment).scaleX;

		this._featureTextStyle = this.textStyles.featureTextStyle;
		this._featureTextStyle.wordWrapWidth = descriptionWordWrapWidth;
	}

	public playIntroAnimation(): void {
		this.container.addChild(this._introScreen);
		this.container.addChild(this._introFeatures);
		this.container.addChild(this._extraBackground);

		this._extraBackground.interactive = true;

		// TODO: Add sounds.

		this.updateManager.add(this._featuresAnimations);
		this.updateManager.add(this._screenAnimations);

		this._featuresAnimations.play('FEATURE_1_ENTRY', 0, false);
		this._screenAnimations.play('START', 0, false);

		this._featuresAnimations.setOnceOnComplete(() => {
			this.emitIntroFinished();
			this.playIdleFeature();
			this._playButton.visible = true;
			this._playButton.enable();

			this._prevFeatureButton.visible = true;
			this._prevFeatureButton.enable();
			this._nextFeatureButton.visible = true;
			this._nextFeatureButton.enable();
		});

		this.container.setChildIndex(this._extraBackground, 0);

		this.emitIntroAnimationStarted();

		this.playIntroMusic();
	}

	protected playIntroMusic(): void {
		this.soundPlayer.playMusic('musicLobby');
	}

	public proceed(): void {
		if (this._playButton.interactive) {
			this.onPlayPressed();
		} else if (this._playingExitAnimation) {
			this.skipIntro();
		}
	}

	public previousFeature(): void {
		if (this._nextFeatureButton.interactive) {
			this._currentFeature = (this._currentFeature % this._totalFeatures) + 1;
			// TODO: Add sounds

			this._featuresAnimations.play(`FEATURE_${this._currentFeature}_ENTER_RIGHT`, 0, false);
			this._featuresAnimations.setOnceOnComplete(this.playIdleFeature.bind(this));
		}
	}

	public nextFeature(): void {
		if (this._prevFeatureButton.interactive) {
			// TODO: Add sounds

			this._currentFeature = this._currentFeature - 1 < 1 ? this._totalFeatures : (this._currentFeature - 1) % this._totalFeatures;

			this._featuresAnimations.play(`FEATURE_${this._currentFeature}_ENTER_LEFT`, 0, false);
			this._featuresAnimations.setOnceOnComplete(this.playIdleFeature.bind(this));
		}
	}

	protected emitIntroAnimationStarted(): void {
		this.emit(StartScreenViewEvents.INTRO_STARTED);
	}

	protected emitIntroIdle(): void {
		this.emit(StartScreenViewEvents.INTRO_IDLE);
	}

	protected emitIntroFinished(): void {
		this.emit(StartScreenViewEvents.INTRO_FINISHED);
	}

	protected emitExitAnimationStarted(): void {
		this.emit(StartScreenViewEvents.EXIT_STARTED);
		this.soundPlayer.play('intro');
	}

	protected emitExitAnimationFinished(): void {
		this.emit(StartScreenViewEvents.EXIT_FINISHED);
	}

	protected onExitFinished(): void {
		if (!this._exitFinished) {
			this._exitFinished = true;
			this.emitExitAnimationFinished();
			this.cleanupAll();
		}
	}

	protected skipIntro(): void {
		this.onExitFinished();
		this.soundPlayer.stop('intro');
	}

	protected cleanupAll(): void {
		this.cleanUpAnimation(this._screenAnimations);
		this.cleanUpAnimation(this._featuresAnimations);

		this.safeDestroy(this._introScreen);
		this.safeDestroy(this._introFeatures);
		this.safeDestroy(this._nextFeatureButton);
		this.safeDestroy(this._prevFeatureButton);
		this.safeDestroy(this._extraBackground);
	}

	private safeDestroy(object: PIXI.Sprite | Spine): void {
		if (object && object.destroy && (object as PIXI.Sprite).texture) {
			object.destroy();
		}
	}

	private cleanUpAnimation(animation: SpineAnimation | SpineAnimationMultiTrack): void {
		if (animation && animation.stop) {
			animation.stop();
			this.updateManager.remove(animation);
			animation = null;
		}
	}

	protected setupIntro(): void {
		this._introScreen = new Spine(this.resources.getSpine('STARTSCREEN.json', this.viewType));
		this._introFeatures = new Spine(this.resources.getSpine('FEATURE_SCREENS.json', this.viewType));
		this._extraBackground = SpriteGraphics.makeHiddenRectangle(
			this.resolutionManager.resolution.width,
			this.resolutionManager.resolution.height,
		);

		const introPosition: Position = this.layout.getCenterOfScreen(this.viewType);
		this._introFeatures.position.set(introPosition.x, introPosition.y);

		this._screenAnimations = new SpineAnimationMultiTrack(this._introScreen);
		this._featuresAnimations = new SpineAnimationMultiTrack(this._introFeatures);
		this._screenAnimations.start();
		this._featuresAnimations.start();

		this.setupTranslatedWinUpTo();
	}

	protected setupTranslatedWinUpTo(): void {}

	protected setupInput(): void {
		this.setupPlayButton();
		this.setupFeatureArrows();
	}

	protected setupPlayButton(): void {
		this._playButton = this.spineUtils.createLocalizedSpineButton(
			'PLAY_BUTTON.json',
			this.onPlayPressed.bind(this),
			['PLAY_BUTTON.png', 'PLAY_BUTTON_CLICKED.png', 'PLAY_BUTTON_HOVER.png', 'PLAY_BUTTON_DISABLED.png'],
			['BUTTON', 'PLAY_BUTTON_CLICKED', 'PLAY_BUTTON_HOVER', 'PLAY_BUTTON_DISABLED'],
			true,
			false,
			false,
			false,
		);
		this.spineUtils.replaceElementInSpine(this._introScreen, 'PLAY_BUTTON', this._playButton);
		this._playButton.disable();
		this._playButton.visible = false;
	}

	protected setupFeatureArrows(): void {
		this._nextFeatureButton = this.spineUtils.createSpineButton(
			'ARROW_BUTTON.json',
			this.onNextFeatureClicked.bind(this),
			true,
			false,
			false,
			false,
		);
		this.spineUtils.replaceElementInSpine(this._introScreen, 'ARROW_RIGHT', this._nextFeatureButton);
		this._nextFeatureButton.visible = false;
		this._nextFeatureButton.disable();

		this._prevFeatureButton = this.spineUtils.createSpineButton(
			'ARROW_BUTTON.json',
			this.onPreviousFeatureClicked.bind(this),
			true,
			false,
			false,
			false,
		);
		this.spineUtils.replaceElementInSpine(this._introScreen, 'ARROW_LEFT', this._prevFeatureButton);
		this._prevFeatureButton.visible = false;
		this._prevFeatureButton.disable();

		if (this.device.isMobile) {
			this._nextFeatureButton.setOnClickIsOverState(false);
			this._prevFeatureButton.setOnClickIsOverState(false);
		}
	}

	protected onPlayPressed(): void {
		if (this._playButton.interactive) {
			this.emit(StartScreenView.PLAY_BUTTON_CLICKED);
			this.soundPlayer.play('buttonClick');

			this._playButton.interactive = false;
			this._extraBackground.on('click', this.skipIntro.bind(this));
			this._extraBackground.on('touchend', this.skipIntro.bind(this));
			this.playExitAnimation();
		}
	}

	protected playIdleFeature(): void {
		this._featuresAnimations.play(`FEATURE_${this._currentFeature}_IDLE`, 0, true);
	}

	protected playExitAnimation(): void {
		this._screenAnimations.play('EXIT', 0, false);

		const featuresExitTween: Tween = new Tween(this._introFeatures.position)
			.to({y: +2000}, 500)
			.easing(Easing.Back.InOut)
			.start();

		this.updateManager.add(featuresExitTween);

		this._introScreen.alpha = 1;

		const backgroundScreenRemoval: Tween = new Tween(this._introScreen)
			.to({alpha: 0}, 1500)
			.easing(Easing.Sinusoidal.In)
			.onComplete(() => {
				this.onExitFinished();
			})
			.start();
		this.updateManager.add(backgroundScreenRemoval);

		// TODO: Add sounds.

		this.emitExitAnimationStarted();
		this._playingExitAnimation = true;
	}

	protected onProceedPressed(): void {
		this.soundPlayer.play('buttonClick');

		this.proceed();
	}

	protected onNextFeatureClicked(): void {
		this.soundPlayer.play('buttonClick');

		this.previousFeature();
	}

	protected onPreviousFeatureClicked(): void {
		this.soundPlayer.play('buttonClick');

		this.nextFeature();
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/startscreen/StartScreenView.ts